import type {
  SubEntry,
  LiquidSubEntry,
  FoodSubEntry,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import {
  useMemo,
  useRef,
} from "react"
import type { Aggregate } from "./aggregateSubEntries"
import {
  aggregateFoodSubEntries,
  aggregateLiquidSubEntries,
} from "./aggregateSubEntries"
import type {
  GraphActivity,
  GraphActivityType,
  FavoriteGraphActivity,
} from "./GraphActivity"
import { shouldHideEventFromGraph } from "./shouldHideEventFromGraph"
import {
  useEntryModalPublicContext,
  useEventModalPublicContext,
} from "src/components"
import { useFollowedUsers } from "src/contexts"
import {
  useAccount,
  useFindUserEntries,
  useFindUserEvents,
} from "src/hooks"
import {
  createStartedAtSelector,
  createQueryObject,
} from "src/utils"

const activityTypes: GraphActivityType[] = [
  ActivityType.DeviceChange,
  ActivityType.Exercise,
  ActivityType.Food,
  ActivityType.Insulin,
  ActivityType.Liquid,
  ActivityType.Medication,
  ActivityType.Note,
]

const activityTypesForEventModals = [
  ActivityType.DeviceChange,
  ActivityType.Bolus,
  ActivityType.CGMSessionEnded,
  ActivityType.PumpSuspension,
]

/*
 * This function returns a list of GraphActivity between start and end times
 * that are sorted by descending startedAt.
 */
export function useGraphActivities(
  start: string,
  end: string,
): GraphActivity[] {
  const { openEntryModal } = useEntryModalPublicContext()
  const { openEventModal } = useEventModalPublicContext()
  const accountId = useAccount().data?.id
  const { currentUserId } = useFollowedUsers()

  const entryQueryObject = useMemo(
    () => createQueryObject(
      currentUserId ?? -1,
      {
        selector: createStartedAtSelector(
          start,
          end,
        ),
        sort: [{ started_at: "desc" as const }],
      },
    ),
    [
      currentUserId,
      start,
      end,
    ],
  )

  const {
    result: entries,
    isFetching: isFetchingEntries,
  } = useFindUserEntries(entryQueryObject)

  const eventQueryObject = useMemo(
    () => ({
      ...entryQueryObject,
      selector: {
        ...entryQueryObject.selector,
        event_type: { $nin: [ActivityType.Glucose] },
      },
    }),
    [entryQueryObject],
  )

  const {
    result: events,
    isFetching: isFetchingEvents,
  } = useFindUserEvents(eventQueryObject)

  const lastReturnedValueRef = useRef<GraphActivity[]>([])

  return useMemo(
    () => {
      if (isFetchingEvents || isFetchingEntries) {
        return lastReturnedValueRef.current
      }

      const combinedActivities: GraphActivity[] = []
      let eventIndex = 0
      let entryIndex = 0

      function addEvent() {
        const userEvent = events[eventIndex]

        const activityType = userEvent.event_type as keyof typeof userEvent

        // Don't show events that are noise or not actionable in the graph.
        if (shouldHideEventFromGraph(userEvent)) {
          eventIndex++
          return
        }

        const onClick = activityTypesForEventModals.includes(userEvent.event_type)
          ? (ev: React.MouseEvent) => {
            ev.stopPropagation()
            openEventModal({ userEvent })
          }
          : undefined

        combinedActivities.push({
          accountId: userEvent.account_id,
          key: userEvent.id,
          id: userEvent.id,
          idType: "event",
          startedAt: userEvent.started_at,
          durationInSeconds: userEvent.ended_at
            ? dayjs(userEvent.ended_at).diff(
              userEvent.started_at,
              "seconds",
              true,
            )
            : 0,
          activityType,
          [activityType]: userEvent[activityType],
          onClick,
        } as GraphActivity)

        eventIndex++
      }

      function addEntry() {
        const entry = entries[entryIndex]

        const entryTemplateId = entry.entry_template_id

        // add as a favorite if it is a favorite and belongs to the account holder
        if (entryTemplateId && accountId === entry.account_id) {
          combinedActivities.push({
            accountId: entry.account_id,
            key: `${entry.id}`,
            id: `${entry.id}`,
            idType: "entry",
            startedAt: entry.started_at,
            durationInSeconds: entry.duration_in_seconds,
            entryTemplateId,
            onClick: (ev) => {
              ev.stopPropagation()

              openEntryModal({
                userEntry: entry,
                subEntry: entry.subentries.length > 1 ? undefined : entry.subentries[0],
              })
            },
          } as FavoriteGraphActivity)
        } else {
          const subEntryToGraphActivity = (
            subEntry: SubEntry & Partial<Aggregate>,
          ): GraphActivity => {
            const activityType = subEntry.subentry_type as keyof typeof subEntry

            return {
              accountId: entry.account_id,
              key: `${entry.id}${subEntry._id ?? subEntry.offline_id}`,
              id: `${entry.id}`,
              idType: "entry",
              startedAt: entry.started_at,
              durationInSeconds: subEntry.duration_in_seconds,
              activityType,
              [activityType]: subEntry[activityType],
              onClick: (ev) => {
                ev.stopPropagation()

                openEntryModal({
                  userEntry: entry,
                  subEntry: subEntry.is_aggregate ? undefined : subEntry,
                })
              },
            } as GraphActivity
          }

          let liquidSubEntry: LiquidSubEntry | undefined = undefined
          let foodSubEntry: FoodSubEntry | undefined = undefined

          entry.subentries.forEach((subEntry) => {
            if (
              activityTypes.includes(subEntry.subentry_type as GraphActivityType)
            ) {
              if (subEntry.subentry_type === ActivityType.Food) {
                if (foodSubEntry === undefined) {
                  foodSubEntry = subEntry
                } else {
                  foodSubEntry = aggregateFoodSubEntries(
                    foodSubEntry,
                    subEntry,
                  )
                }
              } else if (subEntry.subentry_type === ActivityType.Liquid) {
                if (liquidSubEntry === undefined) {
                  liquidSubEntry = subEntry
                } else {
                  liquidSubEntry = aggregateLiquidSubEntries(
                    liquidSubEntry,
                    subEntry,
                  )
                }
              } else {
                combinedActivities.push(subEntryToGraphActivity(subEntry))
              }
            }
          })

          if (foodSubEntry) {
            combinedActivities.push(subEntryToGraphActivity(foodSubEntry))
          }

          if (liquidSubEntry) {
            combinedActivities.push(subEntryToGraphActivity(liquidSubEntry))
          }
        }

        entryIndex++
      }

      // sort the items from newest to oldest
      while (eventIndex + entryIndex < events.length + entries.length) {
        const userEvent = events[eventIndex]
        const entry = entries[entryIndex]

        if (userEvent && entry) {
          if (dayjs(userEvent.started_at).isAfter(entry.started_at)) {
            addEvent()
          } else {
            addEntry()
          }
        } else if (userEvent) {
          addEvent()
        } else if (entry) {
          addEntry()
        } else {
          break
        }
      }

      lastReturnedValueRef.current = combinedActivities
      return combinedActivities
    },
    [
      isFetchingEvents,
      isFetchingEntries,
      events,
      entries,
      openEventModal,
      accountId,
      openEntryModal,
    ],
  )
}
